import { logger } from './logger/loggerService';
import { isChrome, isEdge, isSafari } from './utils';

export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'laptop' | 'smart-tv' | 'unknown';
type NetworkType = '2g' | '3g' | '4g' | '5g' | 'wifi' | 'cellular' | 'ethernet' | 'unknown';
interface Profile {
  container: string;
  supported: boolean;
}

interface AudioCodecCapability {
  isSupported: boolean;
  Profile: Profile[];
}
interface StreamingSupport {
  isSupported: boolean;
  formats: string[];
  codecs: string[];
}

export class CustomMediaCapabilities {
  capability: {
    networkType: NetworkType;
    deviceMemory: number | undefined;
    deviceType: DeviceType;
    screenResolution: string;
    streamingTechnology: {
      dash: StreamingSupport;
      hls: StreamingSupport;
    };
    drmCapability: {
      aesSupport: string;
      fairPlayDrmSupport: string;
      playreadyDrmSupport: string;
      widevineDRMSupport: string;
    };
    videoQuality: string;
    videoCodecCapability: {
      Hevc: {
        isSupported: boolean;
        Profile: { name: string; codec: string }[];
      };
      AV1: {
        isSupported: boolean;
        Profile: { name: string; codec: string }[];
      };
    };
    audioCodecCapability: AudioCodecCapability;
  };

  constructor() {
    this.capability = {
      networkType: this.detectNetworkType(),
      screenResolution: '',
      videoQuality: '',
      deviceMemory: this.getDeviceMemory(),
      deviceType: this.getDeviceType(),
      drmCapability: {
        aesSupport: 'no',
        fairPlayDrmSupport: 'no',
        playreadyDrmSupport: 'no',
        widevineDRMSupport: 'no',
      },
      videoCodecCapability: {
        Hevc: {
          isSupported: false,
          Profile: [],
        },
        AV1: {
          isSupported: false,
          Profile: [],
        },
      },
      streamingTechnology: {
        dash: {
          isSupported: false,
          formats: [],
          codecs: [],
        },
        hls: {
          isSupported: false,
          formats: [],
          codecs: [],
        },
      },
      audioCodecCapability: {
        isSupported: false,
        Profile: [],
      },
    };
  }

  private getDeviceMemory(): number | undefined {
    const memoryInGB = (navigator as any)?.deviceMemory;
    return memoryInGB !== undefined ? memoryInGB * 1024 : undefined; // Convert GB to MB
  }

  detectNetworkType = (): NetworkType => {
    const connection =
      (navigator as any)?.connection ||
      (navigator as any)?.mozConnection ||
      (navigator as any)?.webkitConnection;
    console.log(connection, 'connection');

    // Function to determine the network type
    const getNetworkType = (): NetworkType => {
      if (connection) {
        if (connection?.type === 'wifi') return 'wifi';
        if (connection?.type === 'cellular' || connection?.effectiveType) {
          return connection?.effectiveType || 'cellular';
        }
        if (connection?.type === 'ethernet') return 'ethernet';
      }
      return 'unknown';
    };

    // If a connection object exists, attach an event listener to monitor changes
    if (connection) {
      connection.addEventListener('change', () => {
        console.log(`Network type changed to: ${getNetworkType()}`);
      });
    }

    // Return the initial network type
    return getNetworkType();
  };
  private getDeviceType(): DeviceType {
    const userAgent = navigator.userAgent;

    if (/Mobi|Android/i.test(userAgent)) {
      return 'mobile';
    }

    if (
      /iPad/i.test(userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 1 && window.innerWidth <= 1024)
    ) {
      return 'tablet';
    }

    if (/Tablet|PlayBook|Silk/i.test(userAgent)) {
      return 'tablet';
    }

    if (
      /Windows NT|Macintosh|Linux/i.test(userAgent) ||
      (window.innerWidth > 1024 && window.innerHeight > 600)
    ) {
      return 'desktop';
    }

    if (
      /Laptop/i.test(userAgent) ||
      (window.innerWidth > 1024 && window.innerWidth <= 1440 && window.innerHeight > 600)
    ) {
      return 'laptop';
    }

    // Check for smart TVs
    if (/SmartTV|HbbTV|Roku|AppleTV/i.test(userAgent)) {
      return 'smart-tv';
    }

    // If none of the above, return unknown
    return 'unknown';
  }

  async checkDrmSupport() {
    const config = [
      {
        initDataTypes: ['cenc', 'webm', 'keyids', 'mp4', 'm3u8'],
        audioCapabilities: [
          { contentType: 'audio/mp4; codecs="mp4a.40.2"' },
          { contentType: 'audio/mp4; codecs="mp4a.40.5"' },
          { contentType: 'audio/mp4; codecs="ac-3"' },
          { contentType: 'audio/mp4; codecs="ec-3"' },
          { contentType: 'audio/webm; codecs="opus"' },
          { contentType: 'audio/mp4; codecs="mp4a.40.2"', robustness: 'SW_SECURE_DECODE' },
        ],
        videoCapabilities: [
          { contentType: 'video/mp4; codecs="avc1.42E01E"' },
          { contentType: 'video/mp4; codecs="hev1.1.6.L93.B0"' },
          { contentType: 'video/webm; codecs="vp9"' },
          { contentType: 'video/webm; codecs="vp8"' },
          { contentType: 'video/mp4; codecs="av01.0.05M.08"' },
          { contentType: 'video/mp4; codecs="avc1.42E01E"', robustness: 'SW_SECURE_DECODE' },
          { contentType: 'video/mp4; codecs="avc1.42E01E"', robustness: 'HW_SECURE_ALL' },
        ],
      },
    ];

    if (isChrome || isEdge) {
      try {
        await navigator.requestMediaKeySystemAccess('com.widevine.alpha', config);
        this.capability.drmCapability.widevineDRMSupport = 'yes';
      } catch (e) {
        logger.error('No Widevine support:', e);
      }
    }

    if (isEdge) {
      try {
        await navigator.requestMediaKeySystemAccess('com.microsoft.playready', config);
        this.capability.drmCapability.playreadyDrmSupport = 'yes';
      } catch (e) {
        logger.error('No Widevine support:', e);
      }
    }

    try {
      await navigator.requestMediaKeySystemAccess('org.w3.clearkey', config);
      this.capability.drmCapability.aesSupport = 'yes';
    } catch (e) {
      console.error('No ClearKey support:', e);
    }

    if (isSafari) {
      if (window.MediaKeys) {
        try {
          await navigator.requestMediaKeySystemAccess('com.apple.fps.1_0', config);
          this.capability.drmCapability.fairPlayDrmSupport = 'yes';
        } catch (e) {
          logger.error('No FairPlay support:', e);
        }
      } else {
        logger.log('EME not supported');
      }
    }

    console.log('DRM Capabilities:', this.capability.drmCapability);
  }

  checkCodecSupport() {
    const hevcProfiles = [
      { name: 'Main', codec: 'hvc1.1.0.L123.B0' },
      { name: 'Main 10', codec: 'hvc1.1.1.L123.B0' },
      { name: 'Main 12', codec: 'hvc1.1.2.L123.B0' },
      { name: 'Main 4:2:2 10', codec: 'hvc1.2.1.L123.B0' },
      { name: 'Main 4:2:2 12', codec: 'hvc1.2.2.L123.B0' },
      { name: 'Main 4:4:4', codec: 'hvc1.3.0.L123.B0' },
      { name: 'Main 4:4:4 10', codec: 'hvc1.3.1.L123.B0' },
      { name: 'Main 4:4:4 12', codec: 'hvc1.3.2.L123.B0' },
      { name: 'Main 4:4:4 16 Intra', codec: 'hvc1.3.3.L123.B0' },
    ];

    const av1Profiles = [
      { name: 'Main', codec: 'av01.0.00M.08' },
      { name: 'High', codec: 'av01.0.01M.08' },
      { name: 'Professional', codec: 'av01.0.10M.08' },
    ];

    const checkSupport = (profiles: { name: string; codec: string }[]) => {
      return profiles.filter((profile) =>
        MediaSource.isTypeSupported(`video/mp4; codecs="${profile.codec}"`),
      );
    };

    const supportedHEVCProfiles = checkSupport(hevcProfiles);
    const supportedAV1Profiles = checkSupport(av1Profiles);

    this.capability.videoCodecCapability.Hevc.isSupported = supportedHEVCProfiles.length > 0;
    this.capability.videoCodecCapability.Hevc.Profile = supportedHEVCProfiles;

    this.capability.videoCodecCapability.AV1.isSupported = supportedAV1Profiles.length > 0;
    this.capability.videoCodecCapability.AV1.Profile = supportedAV1Profiles;
  }

  private getScreenResolutionAndQuality() {
    const width = window.screen.width;
    const height = window.screen.height;
    const res = `${width} x ${height}`;
    let quality: string;

    if (width >= 3840 && height >= 2160) {
      quality = '8K (Ultra HD)';
    } else if (width >= 2560 && height >= 1440) {
      quality = '4K (Ultra HD)';
    } else if (width >= 1920 && height >= 1080) {
      quality = '1080p (Full HD)';
    } else if (width >= 1280 && height >= 720) {
      quality = '720p (HD)';
    } else if (width >= 854 && height >= 480) {
      quality = '480p (SD)';
    } else {
      quality = 'Lower than 480p (SD)';
    }

    this.capability.screenResolution = res;
    this.capability.videoQuality = quality;
  }

  deviceStreamingTechnology() {
    const supportsDASH = () => {
      return (
        !!window.MediaSource &&
        typeof window.MediaSource.isTypeSupported === 'function' &&
        window.MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"') // Example codec
      );
    };

    const supportsHLS = () => {
      return (
        !!window.MediaSource &&
        (MediaSource.isTypeSupported('application/x-mpegURL') ||
          MediaSource.isTypeSupported('application/vnd.apple.mpegURL'))
      );
    };

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    this.capability.streamingTechnology.dash.isSupported = supportsDASH();
    if (this.capability.streamingTechnology.dash.isSupported) {
      this.capability.streamingTechnology.dash.formats = ['mp4', 'm4a'];
      this.capability.streamingTechnology.dash.codecs = ['avc1', 'aac'];
    }

    this.capability.streamingTechnology.hls.isSupported = isSafari ? true : supportsHLS();
    if (this.capability.streamingTechnology.hls.isSupported) {
      this.capability.streamingTechnology.hls.formats = ['m3u8', 'ts'];
      this.capability.streamingTechnology.hls.codecs = ['avc1', 'aac'];
    }
  }

  async checkAudioCompatibility() {
    if (!navigator.mediaCapabilities) {
      console.log('Media Capabilities API is not supported by this browser.');
      return;
    }

    const profiles = [
      {
        profileName: 'AAC-LC',
        codec: 'mp4a.40.2', // AAC-LC
      },
      {
        profileName: 'HE-AAC v1',
        codec: 'mp4a.40.5', // HE-AAC v1
      },
      {
        profileName: 'HE-AAC v2',
        codec: 'mp4a.40.29', // HE-AAC v2
      },
    ];

    const containers = [
      { container: 'audio/mp4' }, // MP4 container (common for HE-AAC)
      { container: 'audio/webm' }, // WebM container
      { container: 'audio/ogg' }, // Ogg container
    ];

    const audioCodecCapability: any = {};

    for (const profile of profiles) {
      const profileCapability: AudioCodecCapability = {
        isSupported: false,
        Profile: [],
      };

      for (const { container } of containers) {
        const config: any = {
          type: 'file',
          audio: {
            contentType: `${container}; codecs="${profile.codec}"`,
            channels: 2,
            bitrate: 128000,
            samplerate: 48000,
          },
        };

        try {
          const result = await navigator.mediaCapabilities.decodingInfo(config);
          const supported = result.supported;

          if (supported) {
            profileCapability.isSupported = true;
          }

          profileCapability.Profile.push({
            container: container,
            supported: supported,
          });
        } catch (error) {
          logger.error(`Error checking support for ${profile.profileName} in ${container}:`, error);
          profileCapability.Profile.push({
            container: container,
            supported: false,
          });
        }
      }

      audioCodecCapability[profile.profileName] = profileCapability;
    }

    console.log('Audio Codec Capability:', audioCodecCapability);
    this.capability.audioCodecCapability = audioCodecCapability;
  }

  private initialized = false;
  private cachedDrmCapability: string | null = null;
  private cachedVideoCodecCapability: string | null = null;

  // Helper method to build the media capabilities object
  private buildMediaCapabilitiesObject() {
    // Cache the stringified DRM capabilities if not already cached
    if (!this.cachedDrmCapability) {
      const drmCapability = this.capability?.drmCapability || {};
      this.cachedDrmCapability = JSON.stringify({
        aesSupport: drmCapability?.aesSupport || 'no',
        fairPlayDrmSupport: drmCapability?.fairPlayDrmSupport || 'no',
        playreadyDrmSupport: drmCapability?.playreadyDrmSupport || 'no',
        widevineDRMSupport: drmCapability?.widevineDRMSupport || 'no',
      });
    }

    // Cache the stringified video codec capabilities if not already cached
    if (!this.cachedVideoCodecCapability) {
      const videoCodecCapability = this.capability?.videoCodecCapability || {};
      this.cachedVideoCodecCapability = JSON.stringify({
        Hevc: {
          isSupported: (videoCodecCapability.Hevc?.isSupported || false).toString(),
          Profile:
            videoCodecCapability.Hevc?.Profile?.map((profile) => ({
              name: profile?.name,
            })) || [],
        },
        AV1: {
          isSupported: (videoCodecCapability.AV1?.isSupported || false).toString(),
          Profile:
            videoCodecCapability.AV1?.Profile?.map((profile) => ({
              name: profile?.name,
            })) || [],
        },
      });
    }

    return {
      networktype: this.capability?.networkType?.toString() || 'unknown',
      deviceMemory: this.capability?.deviceMemory?.toString() ?? '0',
      deviceType: this.capability?.deviceType?.toString() || 'unknown',
      screenResolution: this.capability?.screenResolution || { width: 0, height: 0 },
      devicestreamingtechnology: JSON.stringify(
        this.capability?.streamingTechnology || {
          dash: { isSupported: false, formats: [], codecs: [] },
          hls: { isSupported: false, formats: [], codecs: [] },
        },
      ),
      drmcapability: this.cachedDrmCapability,
      frameRateCapability: JSON.stringify({
        videoQuality: this.capability?.videoQuality?.toString() || 'unknown',
      }),
      videoCodecCapability: this.cachedVideoCodecCapability,
      audioCodecCapability: JSON.stringify(
        this.capability?.audioCodecCapability || {
          isSupported: false,
          Profile: [],
        },
      ),
    };
  }

  async initialize() {
    if (this.initialized) {
      // Return the capabilities object even if already initialized
      return this.buildMediaCapabilitiesObject();
    }

    this.initialized = true;
    try {
      await Promise.all([this.checkDrmSupport(), this.checkAudioCompatibility()]);
      this.checkCodecSupport();
      this.detectNetworkType();
      this.getDeviceType();
      this.getScreenResolutionAndQuality();
      this.deviceStreamingTechnology();
    } catch (error) {
      logger.error('Error during async initialization:', error);
    }

    return this.buildMediaCapabilitiesObject();
  }
}

const mediaCapabilitiesInstance = new CustomMediaCapabilities();

export const getMediaCapabilities = async () => {
  try {
    const cachedCapabilities = localStorage.getItem('mediaCapabilities');
    if (cachedCapabilities) {
      console.log('Loaded cached capabilities from localStorage');
      return JSON.parse(cachedCapabilities);
    }
    const capabilities = await mediaCapabilitiesInstance.initialize();

    const dataSize = new Blob([JSON.stringify(capabilities)]).size;
    console.log(`Data size in bytes: ${dataSize}`);

    localStorage.setItem('mediaCapabilities', JSON.stringify(capabilities));
    logger.log('Saved mediaCapabilities to localStorage');
    return capabilities;
  } catch (error) {
    logger.error('Error in getMediaCapabilities:', error);
    return null;
  }
};
